import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import FilterListIcon from '@material-ui/icons/FilterList';
import { lighten } from '@material-ui/core/styles/colorManipulator';

const CustomTableCell = withStyles(theme => ({
    head: {
        backgroundColor: '#f3f3f3',
        color: '#6d7784',
        fontSize: '0.75rem !important',
    },
    body: {
        fontSize: '0.75rem !important',
    },
    footer: {
        fontSize: '0.75rem !important',
    },
}))(TableCell);

const CustomCheckbox = withStyles({
  root: {
    "&$checked": {
      color: "#1976d2",
    }
  },
  checked: {}
})(Checkbox);

const CustomTablePagination = withStyles(theme => ({
    caption: {
        fontSize: '0.75rem'
    },
    select: {
        fontSize: '0.75rem'
    },
    actions: {
        fontSize: '0.75rem'
    }
}))(TablePagination);

function desc(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function stableSort(array, cmp) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = cmp(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
    return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

class EnhancedTableHead extends React.Component {
    createSortHandler = property => event => {
        this.props.onRequestSort(event, property);
    };

    render() {
        const { onSelectAllClick, order, orderBy, numSelected, rowCount } = this.props;

        return (
            <TableHead>
                <TableRow>
                    {this.props.selectable === true ? (
                        <CustomTableCell padding="checkbox">
                            <CustomCheckbox
                                indeterminate={numSelected > 0 && numSelected < rowCount}
                                checked={numSelected === rowCount}
                                onChange={onSelectAllClick}
                            />
                        </CustomTableCell>
                    ) : null}
                    {this.props.columns.map(column => {
                        return (
                            <CustomTableCell
                                key={column.id}
                                padding={'default'}
                                sortDirection={orderBy === column.id ? order : false}
                            >
                                {column.id === null ? (
                                        <span className="enhanced-table-label" style={{whiteSpace: 'nowrap'}}>{column.label}</span>
                                ) : (
                                    <Tooltip
                                        title="Sort"
                                        placement={'bottom-start'}
                                        enterDelay={300}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={order}
                                            onClick={this.createSortHandler(column.id)}
                                            classes={{icon: "sort-icon"}}
                                        >
                                            <span className="enhanced-table-label" style={{whiteSpace: 'nowrap'}}>{column.label}</span>
                                        </TableSortLabel>
                                    </Tooltip>
                                )}
                            </CustomTableCell>
                        );
                    }, this)}
                </TableRow>
            </TableHead>
        );
    }
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.string.isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const styles = theme => ({
    root: {
        width: '100%',
        marginTop: 0,
    },
    // table: {
    //     minWidth: 1020,
    // },
    tableWrapper: {
        overflowX: 'auto',
    },
});

class EnhancedTable extends React.Component {
    static Cell = CustomTableCell;

    constructor() {
        super()
        this.state = {
            order: 'asc',
            orderBy: 'name',
            selected: [],
            page: 0,
            rowsPerPage: 10,
        };
    }

    componentWillMount() {
        if(this.props.rowsPerPage !== undefined){
            this.setState({
                rowsPerPage: this.props.rowsPerPage
            })
        }
    }

    handleRequestSort = (event, property) => {
        if(property === null){
            return;
        }

        const orderBy = property;
        let order = 'desc';

        if (this.state.orderBy === property && this.state.order === 'desc') {
            order = 'asc';
        }

        this.setState({ order, orderBy });
    };

    handleSelectAllClick = event => {
        if (this.state.selected.length === 0) {
            this.setState(state => ({ selected: this.props.data.map(n => n[this.props.selectableKey || 'id']) }));
            return;
        }
        this.setState({ selected: [] });
    };

    handleClick = (event, row_element) => {
        let id = row_element[this.props.selectableKey || 'id'];
        if(this.props.rowClickHandler){
            return this.props.rowClickHandler(event, row_element);
        }
        const { selected } = this.state;
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];
        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }

        this.setState({ selected: newSelected });
    };

    isSelected = id => this.state.selected.indexOf(id) !== -1;

    handleChangePage = (event, page) => {
        this.setState({ page });
    };

    handleChangeRowsPerPage = event => {
        this.setState({ rowsPerPage: event.target.value });
    };

    componentDidMount() {
        if(this.props.orderBy){
            this.setState({
                orderBy: this.props.orderBy
            })
        }
        if(this.props.order){
            this.setState({
                order: this.props.order
            })
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if(this.props.selectable === true && this.props.onSelectChange !== undefined){
            if(prevState.selected.length !== this.state.selected.length){
                this.props.onSelectChange(this.state.selected);
            }
        }
        if(this.props.selectable === true) {
            if(this.props.selected !== prevProps.selected){
                this.setState({
                    selected: this.props.selected
                })
            }
        }
    }

    render() {
        const { classes, data } = this.props;
        const { order, orderBy, selected, rowsPerPage, page } = this.state;
        const emptyRows = rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

        return (
            <Paper className={classes.root}>
                <div className={classes.tableWrapper}>
                    <Table className={classes.table} aria-labelledby="tableTitle">
                        <EnhancedTableHead
                            columns={this.props.columns}
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={this.handleSelectAllClick}
                            onRequestSort={this.handleRequestSort}
                            rowCount={data.length}
                            selectable={this.props.selectable}
                        />
                        {data.length === 0 ? (
                            <TableBody>
                                <TableRow>
                                    <TableCell className="table-empty-label" colSpan={this.props.columns.length}>
                                        No data is available
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        ) : (
                            <TableBody>
                                {stableSort(data, getSorting(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(n => {
                                        const isSelected = this.isSelected(n[this.props.selectableKey || 'id']);
                                        return (
                                            <TableRow
                                                hover
                                                onClick={event => this.handleClick(event, n)}
                                                role="checkbox"
                                                aria-checked={isSelected}
                                                tabIndex={-1}
                                                key={n.id}
                                                selected={isSelected}
                                            >
                                                {this.props.selectable === true ? (
                                                <TableCell padding="checkbox">
                                                    <CustomCheckbox checked={isSelected} />
                                                </TableCell>
                                                ) : null}
                                                {this.props.buildTableRow(n)}
                                            </TableRow>
                                        )
                                    })
                                }
                            </TableBody>
                        )}
                    </Table>
                </div>
                <CustomTablePagination
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    page={page}
                    backIconButtonProps={{
                        'aria-label': 'Previous Page',
                    }}
                    nextIconButtonProps={{
                        'aria-label': 'Next Page',
                    }}
                    onChangePage={this.handleChangePage}
                    onChangeRowsPerPage={this.handleChangeRowsPerPage}
                />
            </Paper>
        );
    }
}

EnhancedTable.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EnhancedTable);